import { useMemo } from 'react';

import { Flag, FlagPurposeType, FlagStatus, OpenFlagsOnReview } from 'types';

interface ConditionallyApprovedInstallFlagsParams {
  installationQueueFlags?: Flag[] | null;
  mostRecentInstallConditionallyApprovedFlags?: OpenFlagsOnReview[] | null;
  conditionallyApprovalDate?: Date;
}

export const useConditionallyApprovedInstallFlags = ({
  installationQueueFlags,
  mostRecentInstallConditionallyApprovedFlags,
  conditionallyApprovalDate,
}: ConditionallyApprovedInstallFlagsParams): Flag[] => {
  return useMemo(() => {
    if (!installationQueueFlags) {
      return [];
    }

    const baseFlags = installationQueueFlags.filter((flag) => !flag.subjectArchivedOrVoided);

    // Handle flags from conditional approval workflow
    let baseConditionallyApprovedFlags: Flag[] = [];
    if (mostRecentInstallConditionallyApprovedFlags && mostRecentInstallConditionallyApprovedFlags.length > 0) {
      const allowedIds = new Set(
        mostRecentInstallConditionallyApprovedFlags.map((flag: OpenFlagsOnReview) => flag.id),
      );
      baseConditionallyApprovedFlags = baseFlags.filter((flag: Flag) => allowedIds.has(flag.id));
    }

    // Handle post-approval stipulations (added after conditional approval)
    let postCAStipulations: Flag[] = [];
    if (conditionallyApprovalDate) {
      const cutoffDate = new Date(conditionallyApprovalDate);
      postCAStipulations = baseFlags.filter((flag: Flag) => {
        if (flag.purposeType !== FlagPurposeType.stipulation) return false;
        return flag.date && new Date(flag.date) > cutoffDate;
      });
    }

    // Handle open stipulation flags that need to be reviewed (for flagDrivenPackageReview workflow)
    // This handles cases where installation queue is 'approved' but has stipulation flags.
    const caHappenedWithNoSnapshot =
      !!conditionallyApprovalDate && !mostRecentInstallConditionallyApprovedFlags?.length;

    const openUnreviewedStipulations = baseFlags.filter((flag: Flag) => {
      if (flag.status !== FlagStatus.open || flag.purposeType !== FlagPurposeType.stipulation || flag.resolved) {
        return false;
      }

      if (caHappenedWithNoSnapshot) return true;

      return !flag.actionHistory?.some((actionHistory) => actionHistory.flagSubject);
    });

    // Combine all flag types, removing duplicates by ID
    const allFlags = [...baseConditionallyApprovedFlags, ...postCAStipulations, ...openUnreviewedStipulations];
    const uniqueFlags = Array.from(new Map(allFlags.map((flag) => [flag.id?.toString(), flag])).values());

    return uniqueFlags;
  }, [installationQueueFlags, mostRecentInstallConditionallyApprovedFlags, conditionallyApprovalDate]);
};
